import React, { useRef, useEffect, useState } from "react";
import {
  StyledManagePathsGradesPage,
  TableContainerManegePathGrade,
} from "./ManagePathsGrades.styled";
import { PathsGradesDnd } from "./PathsGradesDnd/PathsGradesDnd";
import { ManagementFooter } from "../../../../../Components/Footer/ManagementFooter";
import { NewPathGradeConfigItem, PathsDnd } from "./PathsDnd/PathsDnd";
import { GradesDnd } from "./GradesDnd/GradesDnd";
import { Loader } from "../../../../../../../components/core/Loader/Loader";
import {
  GradeConfigItem,
  PathConfigItem,
  PathGradeConfigItem,
} from "../../../../../../../types/api/configurations.type";
import { compareDataGeneric } from "../../../../../../../utils/comparing.utils";
import { NO_DATA_LABEL } from "../../../../../../../constants/Dictionary";
import { MessageBox } from "../../../../../../../components/MessageBox/MessageBox";
import {
  BatchOperation,
  OperationEnum,
} from "../../../../../../../types/api/generic.type";
import { useEditing } from "../../../../../../../providers/editing.provider";
import {
  useManageGrades,
  useUpdateManageGrades,
} from "../../../../../../../hooks/management/CmSheet/Grades/useManagementGrades.hooks";
import {
  useManagePaths,
  useUpdateManagePaths,
} from "../../../../../../../hooks/management/CmSheet/Paths/useManagementPaths.hooks";
import {
  useManagePathsGrades,
  useUpdateManagePathsGrades,
} from "../../../../../../../hooks/management/CmSheet/PathsGrades/useManagementPathsGrades.hook";

export enum TypeItemPrimary {
  PATH = "PATH",
  GRADE = "GRADE",
}

export type GenericPathGradeItem = {
  id: number;
  name: string;
  position: number;
};

export type JobFamily = {
  order: number;
  name: string;
  initials: string;
};

export type ManagePathsGradesProps = {
  setDisableFilters: (x: boolean) => void;
  configYear: number;
};

type Item = { [key: string]: unknown };

export type FormValues = {
  items: Item[];
};

export const ManagePathsGrades = ({
  setDisableFilters,
  configYear,
}: ManagePathsGradesProps) => {
  const { edit: isEditable, setEdit: setIsEditable } = useEditing();
  const [gradeItems, setGradeItems] = useState<GenericPathGradeItem[]>([]);
  const [pathItems, setPathItems] = useState<GenericPathGradeItem[]>([]);
  const [pathGradeItems, setPathGradeItems] = useState<
    NewPathGradeConfigItem[][]
  >([]);

  const [cacheGradeItems, setCacheGradeItems] = useState<
    GenericPathGradeItem[]
  >([]);
  const [cachePathItems, setCachePathItems] = useState<GenericPathGradeItem[]>(
    [],
  );
  const [cachePathGradeItems, setCachePathGradeItems] = useState<
    NewPathGradeConfigItem[][]
  >([]);

  const [newItemIds, setNewItemIds] = useState<number | null>(null);
  const [editingId, setEditingId] = useState<{
    id: number;
    type: TypeItemPrimary;
  } | null>(null);

  const [newNamePrimary, setNewNamePrimary] = useState<string>("");

  const [activeChildrenItem, setActiveChildrenItem] = useState<{
    row: number;
    column: number;
  } | null>(null);
  const [editingChildrenItem, setEditingChildrenItem] = useState<{
    pathPosition: number;
    gradePosition: number;
    value: string;
  } | null>(null);

  const [isUpdatingAnItem, setIsUpdatingAnItem] = useState(false);

  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const dataIsEmpty = gradeItems.length === 0 && pathItems.length === 0;

  const createMatrix = (
    pathGradeItems: PathGradeConfigItem[],
    gradeItems: GradeConfigItem[],
    pathItems: PathConfigItem[],
  ): (PathGradeConfigItem | null)[][] => {
    const array: (PathGradeConfigItem | null)[][] = [];

    const sortedGrades = [...gradeItems].sort(
      (a, b) => b.position - a.position,
    );
    const sortedPaths = [...pathItems].sort((a, b) => a.position - b.position);

    for (let i = 0; i < sortedPaths.length; i++) {
      let row: (PathGradeConfigItem | null)[] = new Array(
        sortedGrades.length,
      ).fill(null);
      array.push(row);
    }

    pathGradeItems.forEach((item) => {
      const gradeIndex = sortedGrades.findIndex(
        (grade) => grade.id === item.gradeId,
      );
      const pathIndex = sortedPaths.findIndex(
        (path) => path.id === item.pathId,
      );

      if (gradeIndex !== -1 && pathIndex !== -1) {
        array[pathIndex][gradeIndex] = item;
      }
    });

    return array;
  };

  const { data: grades, isFetching: loadingGrades } =
    useManageGrades(configYear);

  const { data: paths, isFetching: loadingPaths } = useManagePaths(configYear);

  const { data: pathsGrades, isFetching: loadingPathsGrades } =
    useManagePathsGrades(configYear);

  const { mutateAsync: updateGrades, isPending: loadingUpdateGrades } =
    useUpdateManageGrades();

  const { mutateAsync: updatePaths, isPending: loadingUpdatePaths } =
    useUpdateManagePaths();

  const {
    mutateAsync: updatePathsGrades,
    isPending: loadingUpdatePathsGrades,
  } = useUpdateManagePathsGrades();

  const loading = loadingGrades || loadingPaths || loadingPathsGrades;
  const updating =
    loadingUpdateGrades || loadingUpdatePaths || loadingUpdatePathsGrades;

  useEffect(() => {
    if (!grades || !paths || !pathsGrades || isEditable) {
      return;
    }

    setGradeItems(grades);
    setPathItems(paths);
    setPathGradeItems(createMatrix(pathsGrades, grades, paths));
  }, [grades, paths, pathsGrades, isEditable]);

  const onSubmit = async () => {
    await onSave();
    setIsEditable(!isEditable);
    setCacheGradeItems([]);
    setCachePathItems([]);
    setCachePathGradeItems([]);
    setDisableFilters(!isEditable);
  };

  const onSave = async () => {
    const gradesResponse = await saveGrades();
    const pathsResponse = await savePaths();
    await savePathsGrades(gradesResponse, pathsResponse);
  };

  const saveGrades = async (): Promise<GradeConfigItem[]> => {
    const compareData = gradeItems.map((item, index) => ({
      id: item.id,
      name: item.name,
      position: index + 1,
    }));

    const result = compareDataGeneric<GradeConfigItem>(
      grades ?? [],
      compareData,
    );

    if (result.length > 0) {
      return updateGrades(result);
    }

    return grades ?? [];
  };

  const savePaths = async (): Promise<PathConfigItem[]> => {
    const compareData = pathItems.map((item, index) => ({
      id: item.id,
      name: item.name,
      position: index + 1,
    }));

    const result = compareDataGeneric<PathConfigItem>(paths ?? [], compareData);

    if (result.length > 0) {
      return updatePaths(result);
    }

    return paths ?? [];
  };

  const cleanPathGradeWithoutParent = (
    operations: BatchOperation<PathGradeConfigItem>[],
    pathGradeItems: PathGradeConfigItem[],
    gradeItems: GradeConfigItem[],
    pathItems: PathConfigItem[],
  ): BatchOperation<PathGradeConfigItem>[] => {
    return operations.filter((item) => {
      if (item.operation === OperationEnum.DELETE) {
        const pathGrade = pathGradeItems.find(
          (pathGrade) => pathGrade.id === item.entityId,
        );
        if (pathGrade) {
          const grade = gradeItems.find(
            (grade) => grade.id === pathGrade.gradeId,
          );
          const path = pathItems.find((path) => path.id === pathGrade.pathId);
          if (grade && path) {
            return true;
          }
          return false;
        }
      }
      return true;
    });
  };

  const updateParentIds = (
    pathGradeItems: NewPathGradeConfigItem[][],
    gradeItems: GradeConfigItem[],
    pathItems: PathConfigItem[],
  ) => {
    const updatedPathGradeItems = pathGradeItems.map((row, columnIndex) => {
      const updatedRow = row.map((cell, rowIndex) => {
        if (cell) {
          const grade = gradeItems[gradeItems.length - (rowIndex + 1)];
          const path = pathItems[columnIndex];
          if (grade && path) {
            return {
              ...cell,
              gradeId: grade.id,
              pathId: path.id,
            };
          }
        }
        return cell;
      });
      return updatedRow;
    });
    return updatedPathGradeItems;
  };

  const savePathsGrades = async (
    gradeItems: GradeConfigItem[],
    pathItems: PathConfigItem[],
  ) => {
    const updatedParentIdsPathGradeItems = updateParentIds(
      pathGradeItems,
      gradeItems,
      pathItems,
    );

    const compareData = updatedParentIdsPathGradeItems
      .flat()
      .filter((item) => item !== null)
      .sort((a, b) =>
        a!.id === undefined ? 1 : b!.id === undefined ? -1 : a!.id - b!.id,
      );

    const sortedPathsGradesData = [...(pathsGrades ?? [])].sort(
      (a, b) => a.id - b.id,
    );

    const result = compareDataGeneric<PathGradeConfigItem>(
      sortedPathsGradesData,
      compareData,
    );

    const cleanedResult = cleanPathGradeWithoutParent(
      result,
      sortedPathsGradesData,
      gradeItems,
      pathItems,
    );

    if (cleanedResult.length > 0) {
      await updatePathsGrades(cleanedResult);
    }
  };

  const toggleCancel = () => {
    setIsEditable(!isEditable);
    setGradeItems(cacheGradeItems);
    setPathItems(cachePathItems);
    setPathGradeItems(cachePathGradeItems.map((row) => [...row]));
    setDisableFilters(!isEditable);
    setEditingId(null);
    setIsUpdatingAnItem(false);
    setNewItemIds(null);
    setActiveChildrenItem(null);
    setEditingChildrenItem(null);
  };

  const toggleEdit = () => {
    setIsEditable(!isEditable);
    setCacheGradeItems(gradeItems);
    setCachePathItems(pathItems);
    setCachePathGradeItems(pathGradeItems.map((row) => [...row]));
    setDisableFilters(!isEditable);
  };

  const handleEdit = (id: number, typeItemPrimary: TypeItemPrimary) => {
    let itemToEdit: GenericPathGradeItem | null = null;
    if (typeItemPrimary === TypeItemPrimary.PATH) {
      const itemsCopy = [...pathItems];
      itemToEdit = itemsCopy.find((item) => item.id === id) ?? null;
    }
    if (typeItemPrimary === TypeItemPrimary.GRADE) {
      const itemsCopy = [...gradeItems];
      itemToEdit = itemsCopy.find((item) => item.id === id) ?? null;
    }

    if (itemToEdit) {
      setNewNamePrimary(itemToEdit.name);
      setEditingId({
        id: id,
        type: typeItemPrimary,
      });
    }
    setIsUpdatingAnItem(true);
  };

  const handleDelete = async (id: number, typeItemPrimary: TypeItemPrimary) => {
    if (typeItemPrimary === TypeItemPrimary.PATH) {
      const position = pathItems.findIndex((item) => item.id === id);
      if (position === -1) return;
      const updatedPathItems = pathItems.filter((item) => item.id !== id);
      let updatedPathGradeItems = pathGradeItems.filter(
        (_, colIndex) => colIndex !== position,
      );
      if (updatedPathItems.length === 0) {
        updatedPathGradeItems = [];
      }
      setPathItems(updatedPathItems);
      setPathGradeItems(updatedPathGradeItems);
    }
    if (typeItemPrimary === TypeItemPrimary.GRADE) {
      const positionReverse = [...gradeItems]
        .reverse()
        .findIndex((item) => item.id === id);
      if (positionReverse === -1) return;
      const updatedGradeItems = gradeItems.filter((item) => item.id !== id);
      let updatedPathGradeItems = [...pathGradeItems].map((row) =>
        row.filter((_, cellIndex) => cellIndex !== positionReverse),
      );
      if (updatedGradeItems.length === 0) {
        updatedPathGradeItems = [];
      }
      setGradeItems(updatedGradeItems);
      setPathGradeItems(updatedPathGradeItems);
    }
  };

  const handleCancelPathGrade = (
    item: GenericPathGradeItem,
    type: TypeItemPrimary,
    isNewItem: boolean,
  ) => {
    if (type === TypeItemPrimary.PATH && isNewItem) {
      const itemsCopy = [...pathItems];
      const newItems = itemsCopy.filter((itemCopy) => itemCopy.id !== item.id);
      const pathGradeItemsCopy = [...pathGradeItems];
      const newPathGradeItems = pathGradeItemsCopy.filter(
        (_, cellIndex) => cellIndex !== pathGradeItems.length - 1,
      );
      setPathGradeItems(newPathGradeItems);
      setPathItems(newItems);
    }
    if (type === TypeItemPrimary.GRADE && isNewItem) {
      const itemsCopy = [...gradeItems];
      const newItems = itemsCopy.filter((itemCopy) => itemCopy.id !== item.id);
      const pathGradeItemsCopy = [...pathGradeItems];
      const newPathGradeItems = pathGradeItemsCopy.map((row) =>
        row.filter((_, cellIndex) => cellIndex !== 0),
      );
      setPathGradeItems(newPathGradeItems);
      setGradeItems(newItems);
    }

    setEditingId(null);
    setIsUpdatingAnItem(false);
    setNewItemIds(null);
  };

  const handleSave = (id: number, typeItemPrimary: TypeItemPrimary) => {
    if (typeItemPrimary === TypeItemPrimary.PATH) {
      const itemsCopy = [...pathItems];
      if (!newNamePrimary) return;

      const updatedItems = itemsCopy.map((item) => {
        if (item.id === id) {
          return { ...item, name: newNamePrimary };
        }
        return item;
      });
      setPathItems(updatedItems);
      setPathGradeItems(
        createMatrix(
          pathGradeItems
            .flat()
            .filter((item) => item !== null) as PathGradeConfigItem[],
          gradeItems,
          updatedItems,
        ),
      );
    }
    if (typeItemPrimary === TypeItemPrimary.GRADE) {
      const itemsCopy = [...gradeItems];
      const updatedItems = itemsCopy.map((item) => {
        if (item.id === id) {
          return { ...item, name: newNamePrimary };
        }
        return item;
      });
      setGradeItems(updatedItems);
      setPathGradeItems(
        createMatrix(
          pathGradeItems
            .flat()
            .filter((item) => item !== null) as PathGradeConfigItem[],
          updatedItems,
          pathItems,
        ),
      );
    }

    setNewItemIds(null);
    setNewNamePrimary("");
    setEditingId(null);
    setIsUpdatingAnItem(false);
  };

  const handleInputChange = (text: string) => {
    setNewNamePrimary(text);
  };

  const pathsGradesDndProps = {
    isEditable,
    newItemIds,
    editingId,
    newNamePrimary,
    isUpdatingAnItem,
    setPathGradeItems,
    setNewItemIds,
    setEditingId,
    setNewNamePrimary,
    setIsUpdatingAnItem,
    handleEdit,
    handleSave,
    handleDelete,
    handleInputChange,
    handleCancel: handleCancelPathGrade,
    editingChildrenItem: !!editingChildrenItem,
    activeChildrenItem: !!activeChildrenItem,
  };

  const scrollRightContainer = () => {
    if (scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      requestAnimationFrame(() => {
        if (container.scrollWidth > container.clientWidth) {
          container.scrollLeft = container.scrollWidth;
        }
      });
    }
  };

  return (
    <StyledManagePathsGradesPage $empty={dataIsEmpty}>
      <Loader loading={loading || updating} className="loader">
        <TableContainerManegePathGrade
          ref={scrollContainerRef}
          $isEmpty={dataIsEmpty}
        >
          <div className="table-row-general">
            <div className="table-cell-empty" />
            <div className="table-cell-paths">
              <PathsDnd
                pathItems={pathItems}
                setPathItems={setPathItems}
                scrollRightContainer={scrollRightContainer}
                {...pathsGradesDndProps}
              />
            </div>
          </div>
          <div className="table-row-general">
            <div>
              <GradesDnd
                gradeItems={gradeItems}
                setGradeItems={setGradeItems}
                {...pathsGradesDndProps}
              />
            </div>
            <div className="table-cell-paths-grades">
              {!dataIsEmpty || isEditable ? (
                <PathsGradesDnd
                  isEditable={isEditable}
                  gradesCount={gradeItems.length}
                  pathsCount={pathItems.length}
                  pathGradeItems={pathGradeItems}
                  setPathGradeItems={setPathGradeItems}
                  setIsUpdatingAnItem={setIsUpdatingAnItem}
                  isUpdatingAnItem={isUpdatingAnItem}
                  gradeItems={gradeItems}
                  pathItems={pathItems}
                  editingMainItem={!!editingId}
                  activeItem={activeChildrenItem}
                  setActiveItem={setActiveChildrenItem}
                  editingItem={editingChildrenItem}
                  setEditingItem={setEditingChildrenItem}
                />
              ) : (
                !loading && <MessageBox message={NO_DATA_LABEL} />
              )}
            </div>
          </div>
        </TableContainerManegePathGrade>
      </Loader>
      <ManagementFooter
        isEditable={isEditable}
        onCancel={toggleCancel}
        onConfirm={onSubmit}
        toggleEdit={toggleEdit}
        disabledSave={isUpdatingAnItem}
      />
    </StyledManagePathsGradesPage>
  );
};
